import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';
import { useState } from 'react';

const TrustCredibility = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const projectExamples = [
    {
      title: 'Industrial Warehouse Complex',
      location: 'Calgary, AB',
      value: '$8.5M',
      savings: '$2.1M saved',
      timeline: '14 months',
      description: '120,000 sq ft steel building with complete interior finishes',
      image: 'https://public.readdy.ai/ai/img_res/edited_060e03733225ad701a3134cea21e6952_6359063f.jpg',
      materials: ['Steel Building Package', 'Concrete Flooring', 'LED Lighting', 'HVAC Systems'],
    },
    {
      title: 'Multi-Family Residential',
      location: 'Toronto, ON',
      value: '$12.3M',
      savings: '$3.7M saved',
      timeline: '18 months',
      description: '156-unit apartment complex with premium finishes',
      image: 'https://public.readdy.ai/ai/img_res/fe54e34274f3a6f2fbb5ee0ad840cf80.jpg',
      materials: ['Windows & Doors', 'Kitchen Packages', 'Flooring', 'Bathroom Fixtures'],
    },
    {
      title: 'Boutique Hotel Development',
      location: 'Vancouver, BC',
      value: '$6.8M',
      savings: '$2.4M saved',
      timeline: '12 months',
      description: '85-room hotel with custom furniture and finishes',
      image: 'https://readdy.ai/api/search-image?query=elegant%20boutique%20hotel%20building%20exterior%20with%20modern%20design%20and%20large%20windows%20in%20clean%20architectural%20setting%20with%20simple%20background%20professional%20photography&width=600&height=400&seq=project-003&orientation=landscape',
      materials: ['Furniture Packages', 'Custom Cabinetry', 'Tile & Stone', 'Lighting Fixtures'],
    },
  ];

  const faqs = [
    {
      question: 'How do you ensure quality when sourcing internationally?',
      answer: 'We implement a rigorous 5-stage quality control process: (1) Factory certification and audit before partnership, (2) Pre-production sample approval, (3) During-production inspection at 50% completion, (4) Final inspection before shipping, and (5) Third-party verification for critical components. All materials meet or exceed Canadian and US building codes, with full documentation and certifications provided.',
    },
    {
      question: 'What are typical delivery timelines?',
      answer: 'Standard delivery timelines vary by product category: Steel buildings: 8-12 weeks, Windows & doors: 6-10 weeks, Kitchen cabinets: 8-10 weeks, Flooring & tile: 4-8 weeks, Furniture packages: 10-14 weeks. These include manufacturing, shipping, customs clearance, and delivery to your jobsite. Rush orders can be accommodated with expedited manufacturing and air freight options.',
    },
    {
      question: 'Is there a minimum order requirement?',
      answer: 'Minimum order values vary by product category to ensure cost-effectiveness: Steel buildings: $50,000+, Kitchen packages: $25,000+ (typically 5+ units), Windows & doors: $15,000+, Flooring: $10,000+, Furniture: $20,000+. For smaller projects, we can often combine orders or recommend local alternatives. Contact us to discuss your specific needs.',
    },
    {
      question: 'What happens if materials are damaged or defective?',
      answer: 'All shipments include comprehensive insurance coverage. If damage occurs during transit, we file claims immediately and arrange replacement at no cost to you. For manufacturing defects discovered after installation, our factory partnerships include warranty coverage (typically 1-10 years depending on product). We handle all warranty claims, replacement logistics, and communication with manufacturers on your behalf.',
    },
    {
      question: 'How do you handle customs and import regulations?',
      answer: 'Our in-house customs brokerage team manages all import documentation, duty calculations, and regulatory compliance. We handle: HS code classification, customs bond arrangements, duty and tax payment, FDA/EPA/DOT compliance (where applicable), and coordination with CBSA (Canada) or CBP (USA). You receive a single landed cost quote that includes all fees—no surprises at the border.',
    },
    {
      question: 'What payment terms do you offer?',
      answer: 'Standard terms: 30% deposit upon order confirmation, 60% upon production completion (before shipping), 10% upon delivery and inspection. For established clients, we offer: Net 30-60 day terms after credit approval, Progress payment schedules for large projects ($500K+), Letter of credit arrangements for international buyers. All major payment methods accepted including wire transfer, ACH, and corporate credit cards.',
    },
    {
      question: 'Can you match my architectural specifications exactly?',
      answer: 'Yes. We work directly with factories to manufacture custom products to your exact specifications. Provide us with architectural drawings, material specs, finish samples, or brand equivalents, and we\'ll source or manufacture matching products. Our team reviews all technical requirements before quoting to ensure feasibility and code compliance.',
    },
    {
      question: 'What if my project timeline changes?',
      answer: 'We build flexibility into every project: Before production starts: Full refund minus 5% processing fee or reschedule at no cost. During production: We can pause manufacturing for up to 30 days or arrange warehouse storage. After production: Free storage for 30 days, then nominal monthly storage fees. We work with your schedule to minimize costs and maintain material quality.',
    },
  ];

  const riskMitigation = [
    {
      icon: 'ri-shield-check-line',
      title: 'Comprehensive Insurance',
      description: 'Full cargo insurance from factory to jobsite, covering damage, loss, and delays.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Detailed Contracts',
      description: 'Clear terms, specifications, timelines, and remedies documented in every agreement.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Project Manager',
      description: 'Single point of contact managing your entire order from quote to delivery.',
    },
    {
      icon: 'ri-time-line',
      title: 'Real-Time Tracking',
      description: 'Live shipment tracking and proactive updates at every milestone.',
    },
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-xs font-semibold text-secondary-600 tracking-widest uppercase mb-4">
            PROVEN TRACK RECORD
          </div>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl text-gray-900 mb-6">
            Built on <span className="font-semibold">Trust</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Delivering results for developers, contractors, and investors across North America with transparency, reliability, and proven cost savings.
          </p>
        </motion.div>

        {/* Project Examples */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-display font-semibold text-3xl text-gray-900 mb-10 text-center"
          >
            Recent Project Success Stories
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projectExamples.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.6 }}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative w-full h-56">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    {project.savings}
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="font-display font-semibold text-xl text-gray-900 mb-2">
                    {project.title}
                  </h4>
                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <i className="ri-map-pin-line mr-1"></i>
                    {project.location}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{project.description}</p>
                  <div className="flex items-center justify-between text-sm mb-4">
                    <div>
                      <div className="text-xs text-gray-500">Project Value</div>
                      <div className="font-semibold text-gray-900">{project.value}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Timeline</div>
                      <div className="font-semibold text-gray-900">{project.timeline}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Risk Mitigation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mb-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10"
        >
          <h3 className="font-display font-semibold text-3xl text-gray-900 mb-8 text-center">
            How We Protect Your Investment
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {riskMitigation.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mx-auto mb-4">
                  <i className={`${item.icon} text-white text-3xl`}></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <h3 className="font-display font-semibold text-3xl text-gray-900 mb-10 text-center">
            Frequently Asked Questions
          </h3>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-secondary-300 transition-colors"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <i
                    className={`${
                      openFAQ === index ? 'ri-subtract-line' : 'ri-add-line'
                    } text-2xl text-secondary-600 flex-shrink-0 transition-transform`}
                  ></i>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Delivery Timeline Expectations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-20 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-10 text-white"
        >
          <h3 className="font-display font-semibold text-3xl mb-8 text-center">
            Typical Project Timeline
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: '1', title: 'Quote & Planning', time: '1-3 days' },
              { step: '2', title: 'Order & Deposit', time: '1 day' },
              { step: '3', title: 'Manufacturing', time: '4-10 weeks' },
              { step: '4', title: 'Shipping & Customs', time: '2-4 weeks' },
              { step: '5', title: 'Delivery', time: '1-2 days' },
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full text-xl font-bold mx-auto mb-3">
                  {phase.step}
                </div>
                <h4 className="font-semibold mb-1">{phase.title}</h4>
                <p className="text-sm text-white/70">{phase.time}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-white/80 mt-8 text-sm">
            Total timeline: 7-17 weeks from order to delivery, depending on product category and customization requirements.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustCredibility;
